// ---------------------------------------------------------------------------
// The notification bell and the backup screen.
//
// The badge count is polled; the full list is not. That split matters on a
// till: the count is one small query the shell can afford to repeat, while the
// list is only fetched when somebody actually opens the panel.
//
// Nothing here filters by permission. The SERVER decides what this person may
// see and sends only that, so a cashier's panel is genuinely a different set
// of rows rather than the owner's list with things hidden.
// ---------------------------------------------------------------------------

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import type {
  BackupRecord,
  BackupStatus,
  NotificationCounts,
  ShopNotification,
} from '../types';

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export function useNotificationCounts(enabled = true) {
  return useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: async () => {
      const response = await api.get<{ counts: NotificationCounts }>('/notifications/count');
      return response.counts;
    },
    enabled,
    staleTime: 60_000,
    // Every two minutes. Often enough that a low-stock warning reaches
    // somebody the same morning; rare enough to be invisible on the till.
    refetchInterval: 120_000,
  });
}

export function useNotifications(
  options: { includeAcknowledged?: boolean; includeResolved?: boolean } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ['notifications', 'list', options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options.includeAcknowledged) params.set('includeAcknowledged', 'true');
      if (options.includeResolved) params.set('includeResolved', 'true');

      const response = await api.get<{ notifications: ShopNotification[] }>(
        `/notifications?${params.toString()}`,
      );
      return response.notifications;
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useAcknowledgeNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post<{ acknowledged: boolean }>(`/notifications/${id}/acknowledge`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useAcknowledgeAllNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<{ acknowledged: number }>('/notifications/acknowledge-all'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/// Re-derive every notification from the current state of the shop.
///
/// The button beside the panel for somebody who has just topped up the stock
/// and wants the warning to go away now rather than at the next sweep.
export function useSweepNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.post<{ result: { raised: number; resolved: number } }>('/notifications/sweep'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Backups
// ---------------------------------------------------------------------------

export function useBackupStatus(enabled = true) {
  return useQuery({
    queryKey: ['backups', 'status'],
    queryFn: async () => {
      const response = await api.get<{ status: BackupStatus }>('/backups/status');
      return response.status;
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useBackupHistory(enabled = true) {
  return useQuery({
    queryKey: ['backups', 'history'],
    queryFn: async () => {
      const response = await api.get<{ backups: BackupRecord[] }>('/backups');
      return response.backups;
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useRunBackup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (note?: string) =>
      api.post<{ backup: BackupRecord & { deletedFiles: string[] }; message: string }>(
        '/backups/run',
        { note },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['backups'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/// Log a backup somebody took at the terminal.
export function useRecordBackup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { succeeded: boolean; fileName?: string; note?: string }) =>
      api.post<{ backup: BackupRecord; message: string }>('/backups/record', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['backups'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
